import type { Caption } from '@remotion/captions';

import type { CaptionTranslationCue } from '@/shared/apis/editor/clip-types';

/**
 * 번역 자막 cue → 표시용 `Caption` 변환.
 *
 * 번역문에는 단어 타이밍이 없어 cue 1개 = `Caption` 1개로 두고, `captionGroupId` 로 cue 경계를
 * 고정해 자막 한 장에 cue 하나만 뜨게 한다(`splitCaptionsByGap` 의 명시 그룹 분리).
 * 생성 시 hydration 과 에디터 온디맨드 번역이 같은 결과를 내도록 이 파일 하나만 쓴다.
 */

/** cue 타이밍이 깨졌을 때 보장하는 최소 표시 길이(ms). 원문 단어 경로와 같은 값. */
const MIN_TRANSLATION_CUE_DURATION_MS = 10;

const toValidNumber = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * cue 배열을 절대 시간 `Caption[]` 로 만든다.
 *
 * @param cues 캡션 시작 기준 0-based 상대 ms 를 가진 cue 목록
 * @param baseMs 캡션 시작의 절대 시간(타임라인 기준 videoStart)
 */
export const buildCaptionsFromTranslationCues = ({
  cues,
  baseMs,
}: {
  cues: readonly CaptionTranslationCue[] | null | undefined;
  baseMs: number;
}): Caption[] => {
  if (!Array.isArray(cues) || cues.length === 0) return [];

  const captions: Caption[] = [];

  cues.forEach((cue, index) => {
    // 줄바꿈은 원문 경로와 같이 공백으로 바꾼다(줄나눔은 CSS text-wrap: balance 담당).
    const text = (cue?.text ?? '').replace(/\n/g, ' ').trim();
    if (!text) return;

    const relativeStart = Math.max(0, toValidNumber(cue?.start) ?? 0);
    const relativeEnd = toValidNumber(cue?.end);
    const effectiveEnd =
      relativeEnd !== null && relativeEnd > relativeStart
        ? relativeEnd
        : relativeStart + MIN_TRANSLATION_CUE_DURATION_MS;

    const startMs = baseMs + Math.round(relativeStart);
    const endCandidate = baseMs + Math.round(effectiveEnd);
    const endMs =
      endCandidate > startMs
        ? endCandidate
        : startMs + MIN_TRANSLATION_CUE_DURATION_MS;

    captions.push({
      // 원문 경로의 자막 텍스트가 모두 선행 공백을 갖는 것과 같은 형태로 맞춘다.
      text: ` ${text}`,
      startMs,
      endMs,
      timestampMs: Math.round((startMs + endMs) / 2),
      confidence: null,
      // Caption 은 @remotion/captions 외부 타입이라 확장 불가 — extra property 로 동봉한다
      // (deck-captions 의 captionGroupId 와 같은 계약).
      ...{ captionGroupId: `t${index}` },
    });
  });

  return captions;
};
